import jsCookie from 'js-cookie'

import {countryCookieName} from '../constants/http'
import {parseCountry} from '../countries'
import {routeTo} from '../router'
import {readDebugOverrides} from './debug'

/*
 * The visitor's country, as Cloudflare geolocates their request. Cloudflare adds it as the
 * `cf-ipcountry` header to the request it forwards to us, and only there: the browser never sees
 * it, and a page cached at the edge is one copy served to everyone, so it cannot carry it.
 *
 * - A page rendered per request reads the header itself.
 * - The browser asks `/api/country` (`getVisitorCountry`). That response is never cached, so it can
 *   read the header per visitor, and it stores the answer in the `country` cookie.
 *
 * Both read the cookie first (`resolveRequestCountry`): the country is a fact about the visitor,
 * not a preference, decided once and kept. So a server render and the browser always agree, and
 * later visits make no request. The browser asks at most once per page load; if the request fails
 * or takes over 2 s, the country is unknown for that load and nothing is stored, so the next load
 * tries again. A country simulated from the debug modal overrides all of this.
 *
 * See docs/visitor-preferences.md.
 */

const COUNTRY_REQUEST_TIMEOUT = 2000

export const resolveRequestCountry = (
  cookieValue: string | null | undefined,
  headerValue: string | null | undefined,
) => parseCountry(cookieValue) ?? parseCountry(headerValue)

const requestCountry = async () => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), COUNTRY_REQUEST_TIMEOUT)
  try {
    const response = await fetch(routeTo.api.country(), {signal: controller.signal})
    return response.ok ? parseCountry((await response.json()).country) : undefined
  } catch {
    return undefined
  } finally {
    clearTimeout(timeout)
  }
}

const loadVisitorCountry = async () =>
  readDebugOverrides().country ??
  parseCountry(jsCookie.get(countryCookieName)) ??
  (await requestCountry())

let visitorCountry: Promise<string | undefined> | undefined

export const getVisitorCountry = () => {
  visitorCountry ??= loadVisitorCountry()
  return visitorCountry
}
